import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import {
  WaterDrop,
  Science,
  Assessment,
  CalendarToday,
  TrendingUp
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../utils/api';

function Analytics() {
  const { devices } = useAuth();
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const dosingDevices = (devices || []).filter((d) => d.deviceType !== 'irrigation');
  const selectedDeviceObj = dosingDevices.find(d => d.deviceId === selectedDevice);
  const isTds = selectedDeviceObj?.ecUnit === 'TDS';


  // Helper function to format date in local timezone (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    if (typeof date === 'string') return date;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Fetch daily analytics data for a specific device and date
  const fetchAnalyticsData = useCallback(async (deviceId, date, timezone = selectedTimezone) => {
    try {
      setLoading(true);
      setError(null);
      
      const dateStr = formatDateForAPI(date);
      const params = new URLSearchParams({
        date: dateStr,
        timezone: timezone
      });
      
      const response = await api.get(`/data/${deviceId}/analytics/daily?${params.toString()}`);
      if (response.data.success) {
        setAnalyticsData(response.data.data);
      } else {
        throw new Error('Failed to fetch analytics data');
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedTimezone]);

  // Initialize with first device and today's date
  useEffect(() => {
    if (dosingDevices.length > 0 && !selectedDevice) {
      const firstDevice = dosingDevices[0].deviceId;
      setSelectedDevice(firstDevice);
      fetchAnalyticsData(firstDevice, selectedDate, selectedTimezone);
    }
    if (dosingDevices.length === 0) {
      setLoading(false);
      setError('No dosing devices available for analytics.');
    }
  }, [dosingDevices, selectedDevice, selectedDate, selectedTimezone, fetchAnalyticsData]);

  // Handle device selection change
  const handleDeviceChange = (event) => {
    const deviceId = event.target.value;
    setSelectedDevice(deviceId);
    fetchAnalyticsData(deviceId, selectedDate, selectedTimezone);
  };

  // Handle date selection change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (selectedDevice) {
      fetchAnalyticsData(selectedDevice, date, selectedTimezone);
    }
  };

  // Set to today's date
  const handleTodayClick = () => {
    const today = new Date();
    setSelectedDate(today);
    if (selectedDevice) {
      fetchAnalyticsData(selectedDevice, today, selectedTimezone);
    }
  };

  // Format date range with timezone awareness
  const formatDateRange = (dateRange) => {
    if (!dateRange || !dateRange.start || !dateRange.end) return 'No data available';
    
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    
    const options = {
      timeZone: selectedTimezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    
    const startTime = start.toLocaleTimeString('en-US', options);
    const endTime = end.toLocaleTimeString('en-US', options);
    
    return `${startTime} - ${endTime}`;
  };

  // Transform hourly trends data for charts with timezone awareness
  const transformTrendsData = (hourlyTrends) => {
    if (!hourlyTrends || !Array.isArray(hourlyTrends)) return [];
    
    return hourlyTrends.map(item => {
      const hourDate = new Date(item.hour);
      return {
        hour: hourDate.getHours(),
        time: hourDate.toLocaleTimeString('en-US', { 
          timeZone: selectedTimezone,
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }),
        avgPh: parseFloat(item.avgPh || 0),
        avgEc: parseFloat(item.avgEc || 0),
        readingCount: item.readingCount || 0
      };
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading analytics data...</Typography>
      </Box>
    );
  }

  if (error) {
    const isNoDosingDevices = typeof error === 'string' && error.toLowerCase().includes('no dosing devices');
    return (
      <Box sx={{ p: 3 }}>
        {isNoDosingDevices ? (
          <Paper
            sx={{
              p: 4,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              maxWidth: 720,
              mx: 'auto',
              mt: 4
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
              <TrendingUp sx={{ fontSize: '3rem', color: '#9e9e9e' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                No dosing devices available
              </Typography>
              <Typography variant="body1" sx={{ color: '#666666', maxWidth: 560 }}>
                Analytics is available for dosing devices (pH/EC). Your account currently has no dosing devices, or only irrigation devices.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button variant="contained" onClick={() => navigate('/settings')}>
                  Go to Settings
                </Button>
                <Button variant="outlined" onClick={() => navigate('/')}>
                  Go to Dashboard
                </Button>
              </Box>
            </Box>
          </Paper>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => selectedDevice && fetchAnalyticsData(selectedDevice, selectedDate, selectedTimezone)}
            >
              Retry
            </Button>
          </>
        )}
      </Box>
    );
  }

  const chartData = transformTrendsData(analyticsData?.hourlyTrends);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 600, 
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <TrendingUp sx={{ fontSize: '2rem', color: '#41ab5d' }} />
          Data Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View hourly trends and daily analytics for your sensor data
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="end">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Device</InputLabel>
              <Select
                value={selectedDevice}
                onChange={handleDeviceChange}
                label="Device"
                sx={{ height: 56 }}
              >
                {dosingDevices.map((device) => (
                  <MenuItem key={device.deviceId} value={device.deviceId}>
                    {device.deviceName || device.deviceId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select Date
              </Typography>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                maxDate={new Date()}
                dateFormat="yyyy-MM-dd"
                customInput={
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ 
                      '& .MuiInputBase-input': { cursor: 'pointer' },
                      '& .MuiInputBase-root': { height: 56 }
                    }}
                  />
                }
              />
            </Box>
          </Grid>
                    
          <Grid item xs={12} sm={12} md={4}>
            <Button
              variant="outlined"
              onClick={handleTodayClick}
              startIcon={<CalendarToday />}
              fullWidth
              sx={{ 
                height: 56,
                borderColor: '#41ab5d',
                color: '#41ab5d',
                '&:hover': {
                  borderColor: '#2e7d32',
                  backgroundColor: 'rgba(65, 171, 93, 0.04)'
                }
              }}
            >
              Today
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {analyticsData ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2, 
                height: '100%',
                border: '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Total Readings
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ 
                    color: '#41ab5d', 
                    fontWeight: 600,
                    mb: 1
                  }}>
                    {analyticsData.summary.totalReadings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateRange(analyticsData.summary.dateRange)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2, 
                height: '100%',
                border: '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    pH Average
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ 
                    color: '#2196f3', 
                    fontWeight: 600,
                    mb: 1
                  }}>
                    {analyticsData.summary.ph.average || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Range: {analyticsData.summary.ph.min || 'N/A'} - {analyticsData.summary.ph.max || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2, 
                height: '100%',
                border: '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    {isTds ? 'TDS Average' : 'EC Average'}
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ 
                    color: '#ff9800', 
                    fontWeight: 600,
                    mb: 1
                  }}>
                    {analyticsData.summary.ec.average || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Range: {analyticsData.summary.ec.min || 'N/A'} - {analyticsData.summary.ec.max || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2, 
                height: '100%',
                border: '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                    Data Quality
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ 
                    color: '#4caf50', 
                    fontWeight: 600,
                    mb: 1
                  }}>
                    {analyticsData.summary.dataQuality || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hours with data
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            {/* pH Trends Chart */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <WaterDrop sx={{ mr: 1, color: 'info.main' }} />
                  pH Hourly Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      domain={[0, 14]}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      labelFormatter={(value) => `Time: ${value}`}
                      formatter={(value) => [value?.toFixed(2), 'pH']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="avgPh" 
                      stroke="#2196f3" 
                      fill="#2196f3" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* EC Trends Chart */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Science sx={{ mr: 1, color: 'warning.main' }} />
                  {isTds ? 'TDS Hourly Trends' : 'EC Hourly Trends'}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      domain={[0, 20]}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      labelFormatter={(value) => `Time: ${value}`}
                      formatter={(value) => [value?.toFixed(1), isTds ? 'TDS (ppm)' : 'EC (μS/cm)']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="avgEc" 
                      stroke="#ff9800" 
                      fill="#ff9800" 
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Reading Count Chart */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                  Hourly Reading Count
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={(value) => `Time: ${value}`}
                      formatter={(value) => [value, 'Readings']}
                    />
                    <Bar dataKey="readingCount" fill="#4caf50" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Statistics Details */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>pH Statistics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Average</Typography>
                    <Typography variant="h6">{analyticsData.summary.ph.average || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Std Deviation</Typography>
                    <Typography variant="h6">{analyticsData.summary.ph.standardDeviation || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Minimum</Typography>
                    <Typography variant="h6">{analyticsData.summary.ph.min || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Maximum</Typography>
                    <Typography variant="h6">{analyticsData.summary.ph.max || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{isTds ? 'TDS Statistics' : 'EC Statistics'}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Average</Typography>
                    <Typography variant="h6">{analyticsData.summary.ec.average || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Std Deviation</Typography>
                    <Typography variant="h6">{analyticsData.summary.ec.standardDeviation || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Minimum</Typography>
                    <Typography variant="h6">{analyticsData.summary.ec.min || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Maximum</Typography>
                    <Typography variant="h6">{analyticsData.summary.ec.max || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No analytics data available for the selected date
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try selecting a different date or device
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default Analytics;
